import { useState, type JSX } from "react";
import { cn } from "./cn";

export interface AvatarProps {
  readonly label: string;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  readonly color?: "orange" | "blue" | "green" | "red" | "purple" | "pink" | "gray" | string;
  readonly size?: "sm" | "md" | "lg" | "xl";
  readonly status?: "active" | "idle" | "offline";
  readonly imageSrc?: string;
  readonly imageAlt?: string;
}

type AvatarTokenColor = "orange" | "blue" | "green" | "red" | "purple" | "pink" | "gray";

const colorMap: Readonly<Record<AvatarTokenColor, string>> = {
  orange: "bg-accent",
  blue: "bg-blue",
  green: "bg-green",
  red: "bg-red",
  purple: "bg-purple",
  pink: "bg-pink",
  gray: "bg-[#3a3a3a] text-white",
};

const sizeMap: Readonly<Record<NonNullable<AvatarProps["size"]>, string>> = {
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
  xl: "w-[48px] h-[48px] text-base",
};

const statusColorMap: Readonly<Record<NonNullable<AvatarProps["status"]>, string>> = {
  active: "bg-green",
  idle: "bg-amber-idle",
  offline: "bg-content-tertiary",
};

function isTokenColor(color: string): color is AvatarTokenColor {
  return color in colorMap;
}

export function Avatar({
  label,
  color = "orange",
  size = "md",
  status,
  imageSrc,
  imageAlt = "",
}: AvatarProps): JSX.Element {
  const tokenColorClass = isTokenColor(color) ? colorMap[color] : undefined;
  const customColorStyle = tokenColorClass ? undefined : { backgroundColor: color };
  // A blocked/broken image (adblock kills CDN hotlinks, signed URLs expire)
  // must degrade to the initials — never an empty colored circle.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!imageSrc && !imageFailed;

  return (
    <div
      className={cn(
        "relative flex items-center justify-center rounded-full font-semibold text-white shrink-0 overflow-hidden",
        tokenColorClass,
        sizeMap[size],
      )}
      style={customColorStyle}
    >
      {showImage ? (
        <img
          src={imageSrc}
          alt={imageAlt}
          className="h-full w-full object-cover"
          onError={() => {
            setImageFailed(true);
          }}
        />
      ) : (
        <span className="select-none">{label}</span>
      )}
      {status && (
        <span className={cn("absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-surface-secondary", statusColorMap[status])} />
      )}
    </div>
  );
}
